import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import QRCode from 'qrcode';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';
import { storage } from '../config/firebase';
import { ref, uploadString, getDownloadURL } from 'firebase/storage';

interface TransferToQRProps {
  onQRGenerated: (imageUrl: string) => void;
}

const TransferToQR: React.FC<TransferToQRProps> = ({ onQRGenerated }) => {
  const [transferData, setTransferData] = useState({
    nombre: '',
    rut: '',
    banco: '',
    tipoCuenta: '',
    numeroCuenta: '',
    correo: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Sanitize the content
      const content = `
            <p><strong>Nombre:</strong> ${DOMPurify.sanitize(transferData.nombre)}</p>
            <p><strong>RUT:</strong> ${DOMPurify.sanitize(transferData.rut)}</p>
            <p><strong>Banco:</strong> ${DOMPurify.sanitize(transferData.banco)}</p>
            <p><strong>Tipo de Cuenta:</strong> ${DOMPurify.sanitize(transferData.tipoCuenta)}</p>
            <p><strong>Número de Cuenta:</strong> ${DOMPurify.sanitize(transferData.numeroCuenta)}</p>
            <p><strong>Correo:</strong> ${DOMPurify.sanitize(transferData.correo)}</p>`;
  
      // Generate unique identifier
      const uniqueId = uuidv4();
      const fileName = `html_files/${uniqueId}.html`;
  
      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      await uploadString(storageRef, content);
  
      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
  
      // parse url
      const base_url = process.env.REACT_APP_BASE_URL;
      const bucket = 'html_files'
      const token = downloadUrl.split('=').slice(-1)[0];
      const full_url = `${base_url}/transfer.html?bucket=${bucket}&file_id=${uniqueId}&token=${token}`;
      // Generate QR code as data URL
      const qrDataUrl = await QRCode.toDataURL(full_url, {
        errorCorrectionLevel: 'H',
        margin: 1,
        width: 300,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      onQRGenerated(qrDataUrl);
    } catch (error) {
      console.error('Error generating transfer QR code:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Transferencia a QR
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Nombre"
          value={transferData.nombre}
          onChange={(e) => setTransferData({ ...transferData, nombre: e.target.value })}
          required
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Rut"
          value={transferData.rut}
          onChange={(e) => setTransferData({ ...transferData, rut: e.target.value })}
          required
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Banco"
          value={transferData.banco}
          onChange={(e) => setTransferData({ ...transferData, banco: e.target.value })}
          required
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Tipo de cuenta"
          value={transferData.tipoCuenta}
          onChange={(e) => setTransferData({ ...transferData, tipoCuenta: e.target.value })}
          required
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Número de cuenta"
          value={transferData.numeroCuenta}
          onChange={(e) => setTransferData({ ...transferData, numeroCuenta: e.target.value })}
          required
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Correo"
          type="email"
          value={transferData.correo}
          onChange={(e) => setTransferData({ ...transferData, correo: e.target.value })}
        />
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={handleSubmit}
        disabled={loading || !transferData.nombre || !transferData.rut || !transferData.banco || !transferData.tipoCuenta || !transferData.numeroCuenta}
        startIcon={loading ? <CircularProgress size={20} /> : <Send />}
        fullWidth
      >
        {loading ? 'Generando...' : 'Generar QR'}
      </Button>
    </>
  );
};

export default TransferToQR; 